import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { marketingApi } from '../api/marketing';

export interface SignupState {
  isAuthenticated: boolean;
  accessToken: string | null;
  signupId: string | null;
  email: string | null;
  companyName: string | null;
  status: string | null;
  emailVerified: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: SignupState = {
  isAuthenticated: false,
  accessToken: localStorage.getItem('marketing_token'),
  signupId: localStorage.getItem('marketing_signup_id'),
  email: localStorage.getItem('marketing_email'),
  companyName: localStorage.getItem('marketing_company'),
  status: localStorage.getItem('marketing_status'),
  emailVerified: localStorage.getItem('marketing_email_verified') === 'true',
  loading: false,
  error: null,
};

export const signupAsync = createAsyncThunk(
  'signup/create',
  async (data: {
    email: string;
    password: string;
    companyName: string;
    contactFirstName?: string;
    contactLastName?: string;
    contactPhone?: string;
    captchaToken: string;
  }) => {
    const response = await marketingApi.signup(data);
    return response;
  }
);

export const loginAsync = createAsyncThunk(
  'signup/login',
  async (data: { email: string; password: string; captchaToken: string }) => {
    const response = await marketingApi.login(data);
    return response;
  }
);

export const verifyEmailAsync = createAsyncThunk(
  'signup/verifyEmail',
  async (token: string) => {
    const response = await marketingApi.verifyEmail(token);
    return response;
  }
);

export const resendVerificationAsync = createAsyncThunk(
  'signup/resendVerification',
  async (email: string) => {
    const response = await marketingApi.resendVerification(email);
    return response;
  }
);

export const getStatusAsync = createAsyncThunk(
  'signup/getStatus',
  async () => {
    const response = await marketingApi.getStatus();
    return response;
  }
);

const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.accessToken = null;
      state.signupId = null;
      state.email = null;
      state.companyName = null;
      state.status = null;
      state.emailVerified = false;
      localStorage.removeItem('marketing_token');
      localStorage.removeItem('marketing_signup_id');
      localStorage.removeItem('marketing_email');
      localStorage.removeItem('marketing_company');
      localStorage.removeItem('marketing_status');
      localStorage.removeItem('marketing_email_verified');
    },
    clearError: (state) => {
      state.error = null;
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Signup
      .addCase(signupAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.token;
        state.signupId = action.payload.signupId;
        state.email = action.payload.email;
        state.companyName = action.payload.companyName;
        state.status = 'pending_verification';
        state.emailVerified = false;
        state.isAuthenticated = true;
        localStorage.setItem('marketing_token', action.payload.token);
        localStorage.setItem('marketing_signup_id', action.payload.signupId);
        localStorage.setItem('marketing_email', action.payload.email);
        localStorage.setItem('marketing_company', action.payload.companyName);
        localStorage.setItem('marketing_status', 'pending_verification');
        localStorage.setItem('marketing_email_verified', 'false');
      })
      .addCase(signupAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Signup failed';
      })
      // Login
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.token;
        state.signupId = action.payload.signupId;
        state.email = action.payload.email;
        state.companyName = action.payload.companyName;
        state.status = action.payload.status;
        state.emailVerified = action.payload.emailVerified;
        state.isAuthenticated = true;
        localStorage.setItem('marketing_token', action.payload.token);
        localStorage.setItem('marketing_signup_id', action.payload.signupId);
        localStorage.setItem('marketing_email', action.payload.email);
        localStorage.setItem('marketing_company', action.payload.companyName);
        localStorage.setItem('marketing_status', action.payload.status);
        localStorage.setItem('marketing_email_verified', String(action.payload.emailVerified));
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      // Verify Email
      .addCase(verifyEmailAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmailAsync.fulfilled, (state) => {
        state.loading = false;
        state.emailVerified = true;
        state.status = 'onboarding';
        localStorage.setItem('marketing_email_verified', 'true');
        localStorage.setItem('marketing_status', 'onboarding');
      })
      .addCase(verifyEmailAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Email verification failed';
      })
      // Get Status
      .addCase(getStatusAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStatusAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload.status;
        state.emailVerified = action.payload.emailVerified;
        localStorage.setItem('marketing_status', action.payload.status);
        localStorage.setItem('marketing_email_verified', String(action.payload.emailVerified));
      })
      .addCase(getStatusAsync.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { logout, clearError, setAuthenticated } = signupSlice.actions;
export const selectSignup = (state: { signup: SignupState }) => state.signup;
export default signupSlice.reducer;
